import React, { useEffect, useState } from "react";
import "./ProfilePage.css";
import TitleComponent from "../../components/title/Title";
import { EditIcon } from "../../components/icons/Icons";
import { Lock, SmallProfileLogo } from "../../images/Images";
import GradientButton from "../../components/button/Button";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";

// --- INTERFACES ---
interface PersonalDetails {
  id: string;
  full_name: string;
  email: string;
  phone_number: string;
  role: string;
  avatar: string;
}

interface BusinessAddress {
  id: string;
  street: string;
  city: string;
  state: string;
  country: string;
  address_type: string;
}

interface BusinessDetails {
  id: string;
  name: string;
  tagline: string;
  description: string;
  email: string;
  phone: string;
  tax_id: string;
  addresses: BusinessAddress[];
}

interface StoreCode {
  store_code: string;
}

// --- COMPONENT ---
const ProfilePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"personal" | "business" | "security">("personal");
  const [isEditMode, setIsEditMode] = useState(false);

  // Personal Info State
  const [personalDetails, setPersonalDetails] = useState<PersonalDetails | null>(null);
  const [originalPersonalDetails, setOriginalPersonalDetails] = useState<PersonalDetails | null>(null);

  // Business Info State
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails | null>(null);
  const [originalBusinessDetails, setOriginalBusinessDetails] = useState<BusinessDetails | null>(null);

  // Editable address state (first address only)
  const [businessAddress, setBusinessAddress] = useState<BusinessAddress>({
    id: "",
    street: "",
    city: "",
    state: "",
    country: "",
    address_type: "billing",
  });

  const [storeCode, setStoreCode] = useState<string>("");

  const handleEdit = () => {
    setIsEditMode(!isEditMode);
  };

  // Fetch store code
  const getStoreCode = async () => {
    try {
      const response = await axiosInstance.get("/ventures/me");
      if (response.data) {
        setStoreCode(response.data?.data?.store_code);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch personal info
  const fetchPersonalDetails = async () => {
    try {
      const response = await axiosInstance.get("/auth/me");
      setPersonalDetails(response.data.data);
      setOriginalPersonalDetails(response.data.data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Failed to fetch personal details.";
      toast.error(errorMessage);
      console.error("Error fetching personal details:", error);
    }
  };

  // Fetch business info
  const fetchBusinessDetails = async () => {
    try {
      const response = await axiosInstance.get("/ventures/me");
      const data = response.data.data;
      setBusinessDetails(data);
      setOriginalBusinessDetails(data);

      // Set first address if exists
      if (data.addresses && data.addresses.length > 0) {
        setBusinessAddress(data.addresses[0]);
      } else {
        setBusinessAddress({
          id: "",
          street: "",
          city: "",
          state: "",
          country: "",
          address_type: "billing",
        });
      }
    } catch (error: any) {
      toast.error("Failed to fetch business details.");
      console.error("Error fetching business details:", error);
    }
  };

  // Handle personal info submit
  const handleSubmit = async () => {
    if (activeTab === "personal") {
      if (!isEditMode || !personalDetails || !originalPersonalDetails) return;
      const payload: Partial<PersonalDetails> = {};
      Object.keys(personalDetails).forEach((key) => {
        const fieldKey = key as keyof PersonalDetails;
        if (personalDetails[fieldKey] !== originalPersonalDetails[fieldKey]) {
          payload[fieldKey] = personalDetails[fieldKey];
        }
      });
      try {
        await axiosInstance.put("/users", payload);
        toast.success("Profile updated successfully!");
        setOriginalPersonalDetails(personalDetails);
        setIsEditMode(false);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || "Failed to update profile.";
        toast.error(errorMessage);
        console.error("Error updating profile:", error);
      }
    }
    if (activeTab === "business") {
      handleBusinessSubmit();
    }
  };

  // Handle business info change
  const handleBusinessChange = (key: keyof BusinessDetails, value: string) => {
    setBusinessDetails((prev) =>
      prev ? { ...prev, [key]: value } : prev
    );
  };

  // Handle business address change
  const handleBusinessAddressChange = (key: keyof BusinessAddress, value: string) => {
    setBusinessAddress((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle business info submit
  const handleBusinessSubmit = async () => {
    if (!isEditMode || !businessDetails || !originalBusinessDetails) return;
    const payload: Partial<BusinessDetails> = {};

    // Copy all changed fields except addresses
    (Object.keys(businessDetails) as (keyof BusinessDetails)[]).forEach((key) => {
      if (
        key !== "addresses" &&
        businessDetails[key] !== originalBusinessDetails[key]
      ) {
        payload[key] = businessDetails[key];
      }
    });

    // Only include address if any field is filled
    if (
      businessAddress.street ||
      businessAddress.city ||
      businessAddress.state ||
      businessAddress.country
    ) {
      payload.addresses = [
        {
          ...businessAddress,
          address_type: businessAddress.address_type || "billing",
        },
      ];
    }

    try {
      await axiosInstance.put(`/ventures/${businessDetails.id}`, payload);
      toast.success("Business profile updated successfully!");
      setOriginalBusinessDetails(businessDetails);
      setIsEditMode(false);
    } catch (error: any) {
      toast.error("Failed to update business profile.");
      console.error("Error updating business profile:", error);
    }
  };

  useEffect(() => {
    fetchPersonalDetails();
    fetchBusinessDetails();
    getStoreCode();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="profile-page">
      <TitleComponent title="Profile" />

      <div className="profile__page__header">
        <h3
          className={activeTab === "personal" ? "active" : ""}
          onClick={() => setActiveTab("personal")}
        >
          Personal Information
        </h3>
        <h3
          className={activeTab === "business" ? "active" : ""}
          onClick={() => setActiveTab("business")}
        >
          Business Information
        </h3>
        <h3
          className={activeTab === "security" ? "active" : ""}
          onClick={() => setActiveTab("security")}
        >
          Change Password
        </h3>

        <button onClick={handleEdit}>
          <EditIcon />
          <p>{isEditMode ? "Cancel" : "Edit"}</p>
        </button>
      </div>

      <div className="profile__page__body">
        {activeTab === "personal" && (
          <div className="profile__flex">
            <div className="profile__flex__key">
              <h4>Personal Profile</h4>
              <div className="flex__i">
                <img src={SmallProfileLogo} alt="profile" />
                <p>Personal Information</p>
              </div>
            </div>
            <div className="profile__flex__value">
              <h4>Full Name</h4>
              <input
                type="text"
                placeholder="Eg John Doe"
                disabled={!isEditMode}
                value={personalDetails?.full_name || ""}
                onChange={(e) =>
                  setPersonalDetails({
                    ...personalDetails!,
                    full_name: e.target.value,
                  })
                }
              />
              <h4>Email Address</h4>
              <input
                type="email"
                placeholder="Eg Johndoe@gmail.com"
                disabled
                value={personalDetails?.email || ""}
                onChange={(e) =>
                  setPersonalDetails({
                    ...personalDetails!,
                    email: e.target.value,
                  })
                }
              />
              <h4>Phone number</h4>
              <input
                type="text"
                placeholder="+234 6178 88221"
                disabled={!isEditMode}
                value={personalDetails?.phone_number || ""}
                onChange={(e) =>
                  setPersonalDetails({
                    ...personalDetails!,
                    phone_number: e.target.value,
                  })
                }
              />
              <h4>Store code</h4>
              <input
                type="text"
                disabled={true}
                value={storeCode || "No store code"}
              />
            </div>
          </div>
        )}

        {activeTab === "business" && (
          <div className="profile__flex">
            <div className="profile__flex__key">
              <h4>Business Profile</h4>
              <div className="flex__i">
                <img src={SmallProfileLogo} alt="profile" />
                <p>Business Information</p>
              </div>
            </div>
            <div className="profile__flex__value">
              <h4>Business Name</h4>
              <input
                type="text"
                placeholder="E.g John Doe and Sons Groceries"
                disabled={!isEditMode}
                value={businessDetails?.name || ""}
                onChange={e => handleBusinessChange("name", e.target.value)}
              />

              <h4>Business Tagline</h4>
              <input
                type="text"
                placeholder="E.g Fresh products"
                disabled={!isEditMode}
                value={businessDetails?.tagline || ""}
                onChange={e => handleBusinessChange("tagline", e.target.value)}
              />

              <h4>Business description</h4>
              <input
                type="text"
                placeholder="E.g John Doe and Sons Groceries is a sh..."
                disabled={!isEditMode}
                value={businessDetails?.description || ""}
                onChange={e => handleBusinessChange("description", e.target.value)}
              />

              <h4>Business Tax Identification Number</h4>
              <input
                type="text"
                placeholder="E.g TIN-9727981"
                disabled={!isEditMode}
                value={businessDetails?.tax_id || ""}
                onChange={e => handleBusinessChange("tax_id", e.target.value)}
              />

              <h4>Business Phone Number</h4>
              <input
                type="text"
                placeholder="E.g +234 6178 88221"
                disabled={!isEditMode}
                value={businessDetails?.phone || ""}
                onChange={e => handleBusinessChange("phone", e.target.value)}
              />

              <h4>Business Email</h4>
              <input
                type="email"
                placeholder="Eg business@example.com"
                disabled
                value={businessDetails?.email || ""}
              />

              <h4>Business Street Address</h4>
              <input
                type="text"
                placeholder="E.g 123 Main St"
                disabled={!isEditMode}
                value={businessAddress.street}
                onChange={e => handleBusinessAddressChange("street", e.target.value)}
              />

              <h4>City</h4>
              <input
                type="text"
                placeholder="City"
                disabled={!isEditMode}
                value={businessAddress.city}
                onChange={e => handleBusinessAddressChange("city", e.target.value)}
              />

              <h4>State</h4>
              <input
                type="text"
                placeholder="State"
                disabled={!isEditMode}
                value={businessAddress.state}
                onChange={e => handleBusinessAddressChange("state", e.target.value)}
              />

              <h4>Country</h4>
              <input
                type="text"
                placeholder="Country"
                disabled={!isEditMode}
                value={businessAddress.country}
                onChange={e => handleBusinessAddressChange("country", e.target.value)}
              />
            </div>
          </div>
        )}

        {activeTab === "security" && (
          <div className="profile__flex">
            <div className="profile__flex__key">
              <h4>Security</h4>
              <div className="flex__i">
                <img src={Lock} alt="profile" />
                <p>Change Password</p>
              </div>
            </div>
            <div className="profile__flex__value">
              <h4>Current Password</h4>
              <input
                type="password"
                placeholder="Current Password"
                disabled={!isEditMode}
              />
              <h4>New Password</h4>
              <input
                type="password"
                placeholder="New Password"
                disabled={!isEditMode}
              />
              <h4>Confirm Password</h4>
              <input
                type="password"
                placeholder="Confirm Password"
                disabled={!isEditMode}
              />
            </div>
          </div>
        )}
      </div>

      {isEditMode && (
        <div className="profile__save_btn">
          <GradientButton text="Save Changes" onClick={handleSubmit} />
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
